import { FloatingFocusManager, FloatingPortal } from '@floating-ui/react';
import classNames from 'classnames';
import { type ReactNode, useMemo } from 'react';
import { Icon } from '../Icon/Icon';
import { TextInput } from '../TextInput/TextInput';
import { useStableCallback } from '../hooks/useStableCallback';
import { ENTER, SPACE_KEY } from '../utils/keyboard/keyboardKeys';
import { getSelectClassNames } from './Select.styles';
import {
  type SelectMandatoryProps,
  type SelectOption,
  type SelectProps,
  isSelectOptionObject,
} from './Select.types';
import { useSelectList } from './useSelectList';

/**
 * Select control allowing null value
 */
const Optional = <TValue extends string>(props: SelectProps<TValue>) => {
  const {
    className,
    clearable,
    dataTestId,
    name,
    onChange,
    options = [],
    value,
    ...restProps
  } = props;

  const selectClassNames = getSelectClassNames();

  const {
    activeIndex,
    context,
    floatingStyles,
    placement,
    getFloatingProps,
    getItemProps,
    getOptionLabel,
    getReferenceProps,
    isTypingRef,
    listRef,
    menuOpen,
    refs,
    selectedIndex,
    setMenuOpen,
    setSelectedIndex,
  } = useSelectList(options, value, props.disabled);

  const optionLabelsMap = useMemo(() => {
    return options.reduce<Record<TValue, string>>(
      (acc, option) => {
        const key = isSelectOptionObject(option) ? option.value : option;
        acc[key] = isSelectOptionObject(option) ? option.label : option;
        return acc;
      },
      {} as Record<TValue, string>,
    );
  }, [options]);

  const handleSelect = (option: SelectOption<TValue>, index: number) => {
    setSelectedIndex(index);
    setMenuOpen(false);
    onChange(isSelectOptionObject(option) ? option.value : option);
  };

  const handleClear = useStableCallback(() => {
    onChange(null);
  });

  const dropdownPlacement = placement === 'top-start' ? 'top' : 'bottom';

  return (
    <div className={className} data-testid={dataTestId}>
      <div ref={refs.setReference} {...getReferenceProps()}>
        <TextInput
          hasDropdown
          dropdownOpened={menuOpen ? dropdownPlacement : null}
          name={name}
          type='text'
          readOnly
          selectionDisabled
          value={value !== null ? optionLabelsMap[value] : ''}
          onClear={clearable ? handleClear : undefined}
          endAdornment={
            <Icon
              className={classNames(
                'text-default',
                props.disabled
                  ? 'text-status-disabled-dark cursor-default'
                  : 'cursor-pointer',
              )}
              glyph={menuOpen ? 'fa-chevron-up' : 'fa-chevron-down'}
              iconStyle='regular'
              onMouseDown={(e) => e.preventDefault()}
            />
          }
          id={props.inputId}
          {...restProps}
        />
      </div>
      {menuOpen && (
        <FloatingPortal>
          <FloatingFocusManager context={context} modal={false}>
            <div
              ref={refs.setFloating}
              data-testid='select-options'
              className={classNames(
                selectClassNames.optionsContainer,
                placement === 'top-start'
                  ? selectClassNames.optionContainerTop
                  : selectClassNames.optionContainerBottom,
              )}
              style={{
                ...floatingStyles,
              }}
              {...getFloatingProps()}
            >
              {options.map((option, i) => {
                const label = getOptionLabel(option);
                const optionKey = isSelectOptionObject(option)
                  ? option.value
                  : option;
                const isDisabled =
                  isSelectOptionObject(option) && option.disabled === true;
                const tooltip = isSelectOptionObject(option)
                  ? option.tooltip
                  : undefined;

                return (
                  <div
                    key={optionKey}
                    ref={(node) => {
                      listRef.current[i] = node;
                    }}
                    tabIndex={i === activeIndex ? 0 : -1}
                    aria-selected={i === selectedIndex && i === activeIndex}
                    aria-disabled={isDisabled}
                    title={tooltip}
                    className={classNames(
                      selectClassNames.option,
                      isDisabled
                        ? 'text-status-disabled-dark cursor-not-allowed'
                        : [
                            i === activeIndex && 'bg-state-hover',
                            i === selectedIndex
                              ? 'bg-status-selected text-status-selected'
                              : 'text-default',
                          ],
                    )}
                    {...getItemProps({
                      // Handle pointer select.
                      onClick() {
                        if (!isDisabled) {
                          handleSelect(option, i);
                        }
                      },
                      // Handle keyboard select.
                      onKeyDown(event) {
                        if (
                          !isDisabled &&
                          (event.key === ENTER ||
                            (event.key === SPACE_KEY && !isTypingRef.current))
                        ) {
                          event.preventDefault();
                          handleSelect(option, i);
                        }
                      },
                    })}
                  >
                    {label}
                  </div>
                );
              })}
            </div>
          </FloatingFocusManager>
        </FloatingPortal>
      )}
    </div>
  );
};

/**
 * Select control not allowing null value
 */
const Mandatory: <TValue extends string>(
  props: SelectMandatoryProps<TValue>,
) => ReactNode = Optional as any;

export const Select = { Mandatory, Optional };
